/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useState } from "react";
import { DOC_ERROR_SEVERITY } from "@adapptio/lib-blueprint";

import { Layout } from "../../../../ui/components/Layout/Layout";
import { TViewComponent } from "../../../../view/IView";
import { EndpointEditorEditView } from "../EndpointEditorEditView";
import { EndpointEditorOutlineContent, EndpointEditorOutlineToolbar } from "./EndpointEditorEditOutline";
import {
	EndpointEditorDataExplorerContent,
	EndpointEditorDataExplorerToolbar
} from "./EndpointEditorEditDataExplorer";
import { EndpointEditorEditMainToolbar } from "./EndpointEditorEditMainToolbar";
import { EndpointEditorConsoleContent } from "./EndpointEditorEditConsole";
import { EndpointEditorEditInspector } from "./EndpointEditorEditInspector";
import { EndpointEditorEditSettings } from "./EndpointEditorEditSettings";
import { EndpointEditorEditNodePalette } from "./EndpointEditorEditNodePalette";
import { EndpointEditorTesterContent } from "./EndpointEditorEditTester";
import { EndpointEditorEditMainContent } from "./EndpointEditorEditMainContent";
import { EndpointEditorEditSecondaryToolbar } from "./EndpointEditorEditSecondaryToolbar";
import { EndpointEditorConsoleFilter } from "./EndpointEditorEditConsoleFilters";
import { EndpointEditorEditTesterButton } from "./EndpointEditorEditTesterButton";
import { ConsoleCountBadge } from "../../../../ui/components/ConsoleCountBadge";
import { ReferenceListPanel } from "../../../../ui/components/ReferenceList/ReferenceListPanel";
import { ReferenceCountBadge } from "../../../../ui/components/ReferenceList/ReferenceCountBadge";

export const EndpointEditorEditMain: TViewComponent<EndpointEditorEditView> = ({ view }) => {
	const [ severityFilter, setSeverityFilter ] = useState<Set<DOC_ERROR_SEVERITY>>(new Set());
	const endpointModel = useMemo(() => view.getEndpointModel(), [ view ]);
	const layoutState = useMemo(() => view.getLayout(), []);

	const handleFilterSelect = useCallback<(severity: DOC_ERROR_SEVERITY) => void>((severity) => {
		setSeverityFilter((prevState) => {
			prevState.delete(severity) || prevState.add(severity);
			return new Set(prevState);
		});
	}, []);

	const renderComponent = (componentName: string) => {
		switch (componentName) {
			case "outlineToolbar": {
				return <EndpointEditorOutlineToolbar view={view} />;
			}
			case "outlineContent": {
				return <EndpointEditorOutlineContent view={view} />;
			}

			case "dataExplorerToolbar": {
				return <EndpointEditorDataExplorerToolbar />;
			}
			case "dataExplorerContent": {
				return <EndpointEditorDataExplorerContent model={endpointModel} view={view} />;
			}

			case "mainToolbar": {
				return <EndpointEditorEditMainToolbar view={view} />;
			}
			case "mainContent": {
				return <EndpointEditorEditMainContent view={view} />;
			}
			case "secondaryMainToolbar": {
				return <EndpointEditorEditSecondaryToolbar view={view} />;
			}

			case "tester": {
				return <EndpointEditorTesterContent view={view} />;
			}

			case "testerRunner": {
				return <EndpointEditorEditTesterButton view={view} />;
			}

			case "console": {
				return <EndpointEditorConsoleContent model={endpointModel} severityFilter={severityFilter} />;
			}

			case "consoleCount": {
				return <ConsoleCountBadge model={endpointModel} />;
			}

			case "consoleFilters": {
				return (
					<EndpointEditorConsoleFilter
						model={endpointModel}
						severityFilter={severityFilter}
						onFilterSelect={handleFilterSelect}
					/>
				);
			}

			case "references": {
				return <ReferenceListPanel
					resourceId={view.getResource().id}
					dCtx={endpointModel.getDesignContext()}
					resourceService={view.getResourceService()}
				/>;
			}

			case "referencesCount": {
				return <ReferenceCountBadge
					resourceId={view.getResource().id}
					dCtx={endpointModel.getDesignContext()}
					resourceService={view.getResourceService()}
				/>;
			}

			case "nodePalette": {
				return <EndpointEditorEditNodePalette view={view} />;
			}
			case "inspector": {
				return <EndpointEditorEditInspector view={view} />;
			}
			case "settings": {
				return <EndpointEditorEditSettings model={endpointModel} />;
			}

			default: {
				return <p>Error: Undefined panel {componentName}.</p>;
			}
		}
	};

	return <Layout state={layoutState} renderComponent={renderComponent} />;
};
